import { useCallback, useEffect, useMemo, useState } from "react";
import { DeviceForm } from "../components/DeviceForm";
import { DeviceList } from "../components/DeviceList";
import { CameraGrid } from "../components/CameraGrid";
import { PlaybackPanel } from "../components/PlaybackPanel";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { useHeaderAction } from "../components/MobileHeader";
import { IconCameraAdd } from "../components/icons";
import { PullToRefresh } from "../components/PullToRefresh";
import { useDevices } from "../hooks/useDevices";
import { useLiveGps } from "../hooks/useLiveGps";
import { useNativeApp } from "../lib/mobile-ui";
import { usePageRefresh } from "../lib/page-lifecycle";
import { type Device, type DeviceInput } from "../api";

export function CamerasPage({
  active = true,
  onOpenPlayback,
  onOpenLiveTrip,
}: {
  active?: boolean;
  onOpenPlayback?: (device: Device) => void;
  onOpenLiveTrip?: (device: Device) => void;
}) {
  const native = useNativeApp();
  const [ptrEnabled, setPtrEnabled] = useState(false);
  const { devices, loading, error, createDevice, updateDevice, deleteDevice, refresh } = useDevices();
  const { positions, connected } = useLiveGps(active);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Device | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Device | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [playbackId, setPlaybackId] = useState<string | null>(null);

  const onPullRefresh = useCallback(async () => {
    await refresh();
    setRefreshNonce((n) => n + 1);
  }, [refresh]);

  usePageRefresh(
    useCallback(async () => {
      await refresh();
      if (active) setRefreshNonce((n) => n + 1);
    }, [refresh, active]),
  );

  useEffect(() => {
    const mobile = window.matchMedia("(max-width: 900px) and (pointer: coarse)").matches;
    setPtrEnabled(!native && mobile);
  }, [native]);

  useEffect(() => {
    if (!active) return;
    if (selectedId && devices.some((d) => d.id === selectedId)) return;
    if (devices[0]) setSelectedId(devices[0].id);
  }, [active, devices, selectedId]);

  const openAdd = useCallback(() => {
    setEditing(null);
    setFormError(null);
    setFormOpen(true);
  }, []);

  const addAction = useMemo(
    () =>
      native && active && !formOpen ? (
        <button type="button" className="header-action" onClick={openAdd}>
          <IconCameraAdd />
          Add Camera
        </button>
      ) : null,
    [native, active, formOpen, openAdd],
  );
  useHeaderAction(addAction);

  const onSubmit = async (input: DeviceInput) => {
    setFormBusy(true);
    setFormError(null);
    try {
      if (editing) await updateDevice(editing.id, input);
      else await createDevice(input);
      setFormOpen(false);
      setEditing(null);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setFormBusy(false);
    }
  };

  const selected = devices.find((d) => d.id === selectedId) ?? null;
  const playbackDevice = playbackId ? devices.find((d) => d.id === playbackId) ?? selected : null;

  return (
    <PullToRefresh enabled={ptrEnabled} onRefresh={onPullRefresh}>
      <div className="cameras-page">
      <aside className="cameras-side">
        {!native && (
          <div className="drawer-head">
            <h2>Cameras</h2>
            <button type="button" className="primary" onClick={openAdd}>
              Add camera
            </button>
          </div>
        )}
        {error && <p className="form-error">{error}</p>}
        {loading && devices.length === 0 && <p className="empty-grid">Loading cameras…</p>}
        {formOpen ? (
          <DeviceForm
            initial={editing}
            onSubmit={onSubmit}
            onCancel={() => {
              setFormOpen(false);
              setEditing(null);
            }}
            busy={formBusy}
            error={formError}
          />
        ) : (
          <DeviceList
            devices={devices}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onEdit={(d) => {
              setEditing(d);
              setFormError(null);
              setFormOpen(true);
            }}
            onDelete={setPendingDelete}
          />
        )}
      </aside>
      <section className="cameras-live">
        <div className="cameras-live-head">
          <h2>
            {playbackDevice
              ? `Playback · ${playbackDevice.label || playbackDevice.serial_no}`
              : selected
                ? selected.label || selected.serial_no
                : "Live view"}
          </h2>
          <span className={`ws ${connected ? "on" : "off"}`}>{connected ? "GPS connected" : "GPS reconnecting…"}</span>
        </div>
        {playbackDevice ? (
          <PlaybackPanel
            device={playbackDevice}
            embedMap
            onClose={() => setPlaybackId(null)}
            onSwitchDevice={(id) => {
              setSelectedId(id);
              setPlaybackId(id);
            }}
          />
        ) : selected ? (
            <CameraGrid
            devices={[selected]}
            selectedId={selected.id}
            onSelect={setSelectedId}
            gps={positions}
            gpsSocketConnected={connected}
            refreshNonce={refreshNonce}
            pageActive={active}
            onToggleLiveTrip={onOpenLiveTrip ? (id) => {
              const device = devices.find((row) => row.id === id);
              if (device) onOpenLiveTrip(device);
            } : undefined}
            onPlayback={(d) => {
              if (onOpenPlayback) {
                onOpenPlayback(d);
                return;
              }
              setSelectedId(d.id);
              setPlaybackId(d.id);
            }}
          />
        ) : (
          <p className="empty-grid">Select a camera to open live CH1/CH2, listen, and PTT.</p>
        )}
      </section>
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        danger
        title="Delete camera?"
        message={`Delete ${pendingDelete?.label || pendingDelete?.serial_no}? This cannot be undone.`}
        confirmLabel="Delete"
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) void deleteDevice(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
      </div>
    </PullToRefresh>
  );
}
